import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, CheckCircle2, CircleAlert, Clock3, Home, Loader2, Sparkles } from "lucide-react";
import { MemberLayout } from "./MemberLayout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { apiRequest, parseErrorCode, parseErrorMessage, parseErrorStatus, throwIfResNotOk } from "@/lib/queryClient";
import { usePageTitle } from "@/hooks/use-page-title";

type CleaningStatus = "completed" | "pending" | "missed" | "skipped" | "uncovered";

export type MemberCleaningSchedule = {
  propertyName: string;
  weeklyPriceIncreaseCents: number;
  assignments: Array<{
    id: number;
    memberName: string | null;
    scheduledDate: string;
    period: string;
    status: CleaningStatus;
  }>;
  optOuts: Array<{ memberName: string; status: string; effectiveFrom: string }>;
  ownOptOut: { status: string; effectiveFrom: string } | null;
  canOptOut: boolean;
};

type CleaningOptOutResult = {
  messageDelivery?: "queued" | "publish_failed" | "not_applicable";
  messageError?: string;
};

const statusStyle: Record<CleaningStatus, string> = {
  completed: "border-emerald-200 bg-emerald-50 text-emerald-800",
  pending: "border-sky-200 bg-sky-50 text-sky-800",
  missed: "border-rose-200 bg-rose-50 text-rose-800",
  skipped: "border-slate-200 bg-slate-50 text-slate-600",
  uncovered: "border-amber-200 bg-amber-50 text-amber-800",
};

function money(cents: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

function displayDate(value: string): string {
  const dateOnly = String(value).slice(0, 10);
  const date = new Date(`${dateOnly}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateOnly;
  return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export function CleaningContent({ token }: { token?: string }) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const submittingRef = useRef(false);
  const queryKey = token
    ? ["/api/public/cleaning", token, "schedule"]
    : ["/api/member/cleaning/schedule"];
  const scheduleUrl = token
    ? `/api/public/cleaning/${encodeURIComponent(token)}/schedule`
    : "/api/member/cleaning/schedule";
  const optOutUrl = token
    ? `/api/public/cleaning/${encodeURIComponent(token)}/opt-out`
    : "/api/member/cleaning/opt-out";

  const schedule = useQuery<MemberCleaningSchedule>({
    queryKey,
    queryFn: async () => {
      const response = await fetch(scheduleUrl, { credentials: "include" });
      await throwIfResNotOk(response);
      return response.json() as Promise<MemberCleaningSchedule>;
    },
    enabled: token === undefined || token.length > 0,
  });

  const optOut = useMutation({
    mutationFn: async (weeklyPriceIncreaseCents: number) => {
      const response = await apiRequest("POST", optOutUrl, {
        confirmed: true,
        weeklyPriceIncreaseCents,
      });
      return response.json() as Promise<CleaningOptOutResult>;
    },
    onSuccess: async () => {
      setConfirmOpen(false);
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: async () => {
      setConfirmOpen(false);
      await queryClient.invalidateQueries({ queryKey });
    },
    onSettled: () => {
      submittingRef.current = false;
    },
  });

  if (schedule.isLoading) {
    return (
      <div className="flex min-h-48 items-center justify-center" aria-label="Loading cleaning schedule">
        <Loader2 className="h-7 w-7 animate-spin text-brand" />
      </div>
    );
  }

  // An authenticated resident can legitimately have no enabled program. Never
  // infer this from a generic 404 or apply it to a public proof-link failure.
  if (
    token === undefined
    && schedule.isError
    && parseErrorStatus(schedule.error) === 404
    && parseErrorCode(schedule.error) === "program_unavailable"
  ) {
    return (
      <Alert data-testid="cleaning-program-unavailable">
        <Sparkles className="h-4 w-4" />
        <AlertTitle>Cleaning schedule unavailable</AlertTitle>
        <AlertDescription>
          There is no enabled cleaning program for your home right now. Contact your host if you have questions.
        </AlertDescription>
      </Alert>
    );
  }

  if (schedule.isError || !schedule.data) {
    return (
      <Alert variant="destructive" data-testid="cleaning-schedule-error">
        <CircleAlert className="h-4 w-4" />
        <AlertTitle>We couldn't load the cleaning schedule</AlertTitle>
        <AlertDescription className="mt-1">
          Check your connection and try again. If this keeps happening, contact your host or support.
          <Button className="mt-3 block" size="sm" variant="outline" onClick={() => schedule.refetch()}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const data = schedule.data;
  const optOutErrorCode = optOut.isError ? parseErrorCode(optOut.error) : null;
  const stalePrice = optOut.isError && optOutErrorCode === "price_changed";
  const canRequest = data.canOptOut && !data.ownOptOut;

  return (
    <div className="space-y-4" data-testid={token ? "page-public-cleaning" : "page-member-cleaning"}>
      <section className="overflow-hidden rounded-2xl border bg-card shadow-sm">
        <div className="relative bg-brand px-5 py-6 text-brand-foreground">
          <Sparkles className="absolute right-4 top-4 h-12 w-12 opacity-10" aria-hidden="true" />
          <p className="text-xs font-semibold uppercase tracking-[0.16em] opacity-75">House cleaning</p>
          <h1 className="mt-1 pr-12 text-2xl font-bold">{data.propertyName}</h1>
          <p className="mt-2 max-w-md text-sm opacity-85">
            See who's up next and keep track of your home's cleaning rotation.
          </p>
        </div>
      </section>

      {data.ownOptOut ? (
        <Alert className="border-amber-200 bg-amber-50" data-testid="cleaning-own-opt-out">
          <Clock3 className="h-4 w-4 text-amber-700" />
          <AlertTitle className="text-amber-950">
            {data.ownOptOut.status === "pending" ? "Opt-out requested" : `Opt-out ${data.ownOptOut.status}`}
          </AlertTitle>
          <AlertDescription className="text-amber-900">
            {data.ownOptOut.status === "pending"
              ? `Requested ${displayDate(data.ownOptOut.effectiveFrom)} · Awaiting approval.`
              : `Effective ${displayDate(data.ownOptOut.effectiveFrom)}.`}
            {data.ownOptOut.status === "pending"
              ? " Your host or support team still needs to confirm the request."
              : ""}
          </AlertDescription>
        </Alert>
      ) : null}

      {optOut.data?.messageDelivery === "publish_failed" ? (
        <Alert className="border-amber-200 bg-amber-50" data-testid="cleaning-message-delivery-failed">
          <CircleAlert className="h-4 w-4 text-amber-700" />
          <AlertTitle className="text-amber-950">Request recorded, but the confirmation message failed</AlertTitle>
          <AlertDescription className="text-amber-900">
            Your opt-out is still awaiting approval. Contact your host or PadSplit support so they can finish confirming it.
          </AlertDescription>
        </Alert>
      ) : null}

      {optOut.isError ? (
        <Alert variant="destructive" data-testid="cleaning-opt-out-error">
          <CircleAlert className="h-4 w-4" />
          <AlertTitle>{stalePrice ? "The weekly price changed" : "Your request wasn't sent"}</AlertTitle>
          <AlertDescription>
            {stalePrice
              ? "We refreshed the current amount. Review it before confirming again."
              : parseErrorMessage(optOut.error, "Check your connection and try again.")}
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarDays className="h-4 w-4 text-brand" />
            Cleaning schedule
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.assignments.length === 0 ? (
            <p className="py-3 text-sm text-muted-foreground">No cleaning assignments are scheduled yet.</p>
          ) : (
            <ol className="space-y-2">
              {data.assignments.map((assignment) => (
                <li key={assignment.id} className="flex items-center gap-3 rounded-xl border p-3">
                  <div className="min-w-[4.25rem] text-center">
                    <p className="text-xs font-semibold text-foreground">{displayDate(assignment.scheduledDate)}</p>
                    <p className="mt-0.5 text-[11px] capitalize text-muted-foreground">{assignment.period}</p>
                  </div>
                  <div className="h-8 w-px bg-border" aria-hidden="true" />
                  <p className="min-w-0 flex-1 truncate text-sm font-medium">
                    {assignment.status === "uncovered" ? "No one assigned" : assignment.memberName || "House member"}
                  </p>
                  <Badge variant="outline" className={`shrink-0 capitalize ${statusStyle[assignment.status]}`}>
                    {assignment.status}
                  </Badge>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      {data.optOuts.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Household opt-outs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.optOuts.map((item, index) => (
              <div key={`${item.memberName}-${item.effectiveFrom}-${index}`} className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate">{item.memberName}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {item.status === "pending"
                    ? `Requested ${displayDate(item.effectiveFrom)} · Awaiting approval`
                    : `${item.status} · Effective ${displayDate(item.effectiveFrom)}`}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {canRequest ? (
        <Card className="border-brand/25">
          <CardContent className="p-5">
            <div className="flex gap-3">
              <div className="rounded-full bg-brand-muted p-2 text-brand"><Home className="h-5 w-5" /></div>
              <div>
                <h2 className="font-semibold">Prefer not to join the rotation?</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Requesting an opt-out increases your housing price by{" "}
                  <strong className="text-foreground">{money(data.weeklyPriceIncreaseCents)} per week</strong>.
                </p>
              </div>
            </div>
            <Button
              className="mt-4 w-full bg-brand hover:bg-brand-hover"
              onClick={() => setConfirmOpen(true)}
              disabled={optOut.isPending}
              data-testid="button-request-cleaning-opt-out"
            >
              Request cleaning opt-out
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <AlertDialog open={confirmOpen} onOpenChange={(open) => { if (!optOut.isPending) setConfirmOpen(open); }}>
        <AlertDialogContent className="max-w-[calc(100vw-2rem)] rounded-2xl sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm your cleaning opt-out request</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-left">
              <span className="block">
                By continuing, you acknowledge that your housing price will increase by{" "}
                <strong className="text-foreground">{money(data.weeklyPriceIncreaseCents)} per week</strong>.
              </span>
              <span className="block">
                This is a request, not an immediate opt-out. Your host or support team must confirm it before it becomes active.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={optOut.isPending}>Keep my assignment</AlertDialogCancel>
            <AlertDialogAction
              disabled={optOut.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (submittingRef.current || optOut.isPending || !canRequest) return;
                submittingRef.current = true;
                optOut.mutate(data.weeklyPriceIncreaseCents);
              }}
              data-testid="button-confirm-cleaning-opt-out"
            >
              {optOut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              I understand — submit request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function MemberCleaning() {
  usePageTitle("Cleaning");
  return (
    <MemberLayout title="Cleaning">
      <CleaningContent />
    </MemberLayout>
  );
}

export function PublicMemberCleaning({ token }: { token: string }) {
  usePageTitle("Cleaning Schedule");
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b bg-white px-4 py-3">
        <div className="mx-auto max-w-lg">
          <p className="text-sm font-semibold text-brand">PadSplit member cleaning</p>
          <p className="text-xs text-muted-foreground">Secure schedule access</p>
        </div>
      </header>
      <main className="mx-auto w-full max-w-lg px-4 py-5">
        <CleaningContent token={token} />
      </main>
    </div>
  );
}